import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import EventIcon from "@mui/icons-material/Event";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  DEFAULT_CONFIG,
  HomeTheme,
  ThemeConfig,
  ThemeList,
  activateTheme,
  archiveTheme,
  createTheme,
  deactivateTheme,
  deleteTheme,
  listThemes,
  resetTheme,
  scheduleTheme,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { listHomeTabs } from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeTabsService";
import { TabShape } from "../TabShapeEditor";
import { ThemePreview } from "./ThemePreview";
import { ThemeEditor } from "./ThemeEditor";
import { AssetLibrary } from "./AssetLibrary";
import {
  GREEN,
  GREEN_DARK,
  errMsg,
  isoToLocalInput,
  localInputToIso,
  statusChip,
} from "./themeUtils";

type Confirm = {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
  run: () => Promise<void>;
};

const btnGreen = { bgcolor: GREEN, "&:hover": { bgcolor: GREEN_DARK }, textTransform: "none", borderRadius: 2 } as const;
const btnOutline = { textTransform: "none", borderColor: GREEN, color: GREEN_DARK, borderRadius: 2 } as const;

/* ------------------------------ new theme ------------------------------ */

const NewThemeDialog: React.FC<{
  data: ThemeList;
  tabShape: TabShape | null;
  /** Pre-selects "duplicate" with this theme. */
  duplicateOf: HomeTheme | null;
  onClose: () => void;
  onCreated: (t: HomeTheme) => void;
}> = ({ data, tabShape, duplicateOf, onClose, onCreated }) => {
  const [mode, setMode] = React.useState<"preset" | "duplicate" | "blank">(duplicateOf ? "duplicate" : "preset");
  const [presetKey, setPresetKey] = React.useState<string>(data.presets[1]?.key ?? data.presets[0]?.key ?? "");
  const [fromId, setFromId] = React.useState<number | "">(duplicateOf?.id ?? "");
  const [name, setName] = React.useState(duplicateOf ? `${duplicateOf.name} copy` : "");
  const [nameTouched, setNameTouched] = React.useState(!!duplicateOf);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Preset previews come from the seeded preset themes; fall back to Default Green.
  const presetConfig = (key: string): ThemeConfig => {
    const rank = (t: HomeTheme) => (t.created_by === "preset" ? 0 : 1);
    const seeded = data.themes
      .filter((t) => t.preset_key === key)
      .sort((a, b) => rank(a) - rank(b) || a.id - b.id)[0];
    return seeded?.config ?? DEFAULT_CONFIG;
  };

  const suggest = (n: string) => {
    if (!nameTouched) setName(n);
  };

  React.useEffect(() => {
    if (mode === "preset") {
      const p = data.presets.find((x) => x.key === presetKey);
      if (p) suggest(`${p.name} ${new Date().getFullYear()}`);
    } else if (mode === "duplicate") {
      const t = data.themes.find((x) => x.id === fromId);
      if (t) suggest(`${t.name} copy`);
    } else {
      suggest("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, presetKey, fromId]);

  const submit = async () => {
    if (!name.trim()) {
      setError("Give the theme a name.");
      return;
    }
    if (mode === "duplicate" && !fromId) {
      setError("Pick a theme to copy.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const t = await createTheme({
        name: name.trim(),
        ...(mode === "preset" && presetKey ? { preset_key: presetKey } : {}),
        ...(mode === "duplicate" && fromId ? { from_id: Number(fromId) } : {}),
      });
      onCreated(t);
    } catch (e) {
      setError(errMsg(e, "Could not create the theme. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 700 }}>New theme</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Name"
          size="small"
          value={name}
          onChange={(e) => {
            setName(e.target.value.slice(0, 80));
            setNameTouched(true);
          }}
          placeholder="e.g. Deepavali 2026"
          sx={{ width: { xs: "100%", sm: 360 }, mb: 2 }}
          autoFocus
        />
        <Tabs
          value={mode}
          onChange={(_, v) => setMode(v)}
          sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none" }, "& .Mui-selected": { color: `${GREEN_DARK} !important` }, "& .MuiTabs-indicator": { bgcolor: GREEN } }}
        >
          <Tab value="preset" label="Start from a preset" />
          <Tab value="duplicate" label="Copy a theme" />
          <Tab value="blank" label="Blank" />
        </Tabs>
        {mode === "preset" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(232px, 1fr))", gap: 1.5 }}>
            {data.presets.map((p) => {
              const on = presetKey === p.key;
              return (
                <Box
                  key={p.key}
                  role="button"
                  tabIndex={0}
                  aria-pressed={on}
                  onClick={() => setPresetKey(p.key)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setPresetKey(p.key)}
                  sx={{
                    border: `2px solid ${on ? GREEN : "#E3E8E5"}`,
                    borderRadius: 2.5,
                    p: 1,
                    cursor: "pointer",
                    bgcolor: on ? "#F2FBF6" : "#fff",
                    "&:hover": { borderColor: on ? GREEN : "#B9C7BF" },
                    outline: "none",
                    "&:focus-visible": { boxShadow: `0 0 0 3px ${GREEN}55` },
                  }}
                >
                  <ThemePreview config={presetConfig(p.key)} width={212} still tabShape={tabShape} />
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
                    {on && <CheckCircleIcon sx={{ color: GREEN, fontSize: 18 }} />}
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{p.name}</Typography>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
        {mode === "duplicate" && (
          <Box>
            <TextField
              select
              size="small"
              label="Theme to copy"
              value={fromId}
              onChange={(e) => setFromId(Number(e.target.value))}
              sx={{ width: { xs: "100%", sm: 360 }, mb: 2 }}
            >
              {data.themes.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {t.is_default ? " (default)" : ""}
                </MenuItem>
              ))}
            </TextField>
            {fromId !== "" && (
              <ThemePreview config={data.themes.find((t) => t.id === fromId)?.config ?? DEFAULT_CONFIG} width={320} still tabShape={tabShape} />
            )}
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}>
              Copies the look only. The copy starts as a draft with no schedule or cities.
            </Typography>
          </Box>
        )}
        {mode === "blank" && (
          <Box>
            <ThemePreview config={DEFAULT_CONFIG} width={320} still tabShape={tabShape} />
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}>
              Starts from the standard green look with no effects.
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy} sx={btnGreen}>
          {busy ? "Creating…" : "Create and edit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------- schedule ------------------------------- */

const ScheduleDialog: React.FC<{
  theme: HomeTheme;
  onClose: () => void;
  onDone: (list: ThemeList, msg: string) => void;
}> = ({ theme, onClose, onDone }) => {
  const [start, setStart] = React.useState(isoToLocalInput(theme.starts_at));
  const [end, setEnd] = React.useState(isoToLocalInput(theme.ends_at));
  const [priority, setPriority] = React.useState(theme.priority ?? 0);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const startIso = localInputToIso(start);
  const endIso = localInputToIso(end);
  const bad = !!startIso && !!endIso && new Date(endIso) <= new Date(startIso);

  const submit = async () => {
    if (!startIso || !endIso) {
      setError("Pick both a start and an end.");
      return;
    }
    if (bad) {
      setError("The end must be after the start.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const list = await scheduleTheme(theme.id, { starts_at: startIso, ends_at: endIso, priority });
      onDone(list, `“${theme.name}” is scheduled.`);
    } catch (e) {
      setError(errMsg(e, "Could not schedule. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Schedule “{theme.name}”</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
          The theme turns on and off by itself. Times are in your time zone.
        </Typography>
        <Stack spacing={2}>
          <TextField
            type="datetime-local"
            size="small"
            label="Starts"
            InputLabelProps={{ shrink: true }}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <TextField
            type="datetime-local"
            size="small"
            label="Ends"
            InputLabelProps={{ shrink: true }}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            error={bad}
            helperText={bad ? "The end must be after the start" : undefined}
          />
          <TextField
            type="number"
            size="small"
            label="Priority (-100 to 100)"
            value={priority}
            inputProps={{ min: -100, max: 100, step: 1 }}
            onChange={(e) => setPriority(Math.max(-100, Math.min(100, Math.round(Number(e.target.value) || 0))))}
            helperText="Higher wins when two themes overlap."
          />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy} sx={btnGreen}>
          {busy ? "Scheduling…" : "Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------ theme card ------------------------------ */

const ThemeCard: React.FC<{
  theme: HomeTheme;
  showingNow: boolean;
  defaultIsShowing: boolean;
  tabShape: TabShape | null;
  busy: boolean;
  onEdit: () => void;
  onActivate: () => void;
  onUseDefault: () => void;
  onSchedule: () => void;
  onTurnOff: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onReset: () => void;
}> = (p) => {
  const t = p.theme;
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const chip = statusChip(t);
  const on = t.status === "live" || t.status === "scheduled";
  const close = () => setAnchor(null);
  const item = (label: string, icon: React.ReactNode, fn: () => void, danger = false) => (
    <MenuItem
      key={label}
      onClick={() => {
        close();
        fn();
      }}
      sx={danger ? { color: "error.main" } : undefined}
    >
      <ListItemIcon sx={danger ? { color: "error.main" } : undefined}>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );

  const menu: React.ReactNode[] = [];
  if (!t.is_default) menu.push(item("Schedule…", <EventIcon fontSize="small" />, p.onSchedule));
  menu.push(item("Duplicate", <ContentCopyIcon fontSize="small" />, p.onDuplicate));
  if (t.preset_key) menu.push(item("Reset to preset", <RestartAltIcon fontSize="small" />, p.onReset));
  if (!t.is_default && t.status !== "archived") menu.push(item("Archive", <ArchiveOutlinedIcon fontSize="small" />, p.onArchive));
  if (!t.is_default && t.status === "archived") menu.push(item("Move to drafts", <UnarchiveOutlinedIcon fontSize="small" />, p.onTurnOff));
  if (!t.is_default) menu.push(item("Delete", <DeleteOutlineIcon fontSize="small" />, p.onDelete, true));

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: p.showingNow ? `2px solid ${GREEN}` : "2px solid transparent",
        display: "flex",
        flexDirection: "column",
        opacity: t.status === "archived" ? 0.75 : 1,
      }}
    >
      <Box sx={{ p: 1.25, pb: 0, display: "flex", justifyContent: "center", cursor: "pointer" }} onClick={p.onEdit}>
        <ThemePreview config={t.config} width={296} still tabShape={p.tabShape} />
      </Box>
      <Box sx={{ p: 1.75, pt: 1.25, flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 700, fontSize: 15.5, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t.name}
          </Typography>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={`More actions for ${t.name}`} disabled={p.busy}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mt: 0.75 }}>
          <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600, maxWidth: "100%" }} />
          {t.is_default && <Chip size="small" label="Default" variant="outlined" sx={{ fontWeight: 600 }} />}
          {p.showingNow && (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 16, color: `${GREEN} !important` }} />} label="Showing now" variant="outlined" sx={{ borderColor: GREEN, color: GREEN_DARK, fontWeight: 600 }} />
          )}
        </Stack>
        {!t.is_default && (
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}>
            Priority {t.priority} · {t.cities?.length ? t.cities.join(", ") : "All cities"}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={p.onEdit} disabled={p.busy} sx={btnOutline}>
            Edit
          </Button>
          {t.is_default ? (
            !p.defaultIsShowing && (
              <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={p.onUseDefault} disabled={p.busy} sx={btnGreen}>
                Use default
              </Button>
            )
          ) : on ? (
            <Button size="small" variant="outlined" color="inherit" startIcon={<PowerSettingsNewIcon />} onClick={p.onTurnOff} disabled={p.busy} sx={{ textTransform: "none", borderRadius: 2 }}>
              Turn off
            </Button>
          ) : null}
          {!t.is_default && t.status !== "live" && (
            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={p.onActivate} disabled={p.busy} sx={btnGreen}>
              Activate now
            </Button>
          )}
        </Stack>
      </Box>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        {menu}
      </Menu>
    </Card>
  );
};

/* -------------------------------- page -------------------------------- */

export const ThemeStudio: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = React.useState<ThemeList | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [tabShape, setTabShape] = React.useState<TabShape | null>(null);
  const [showArchived, setShowArchived] = React.useState(false);
  const [busyId, setBusyId] = React.useState<number | null>(null);
  const [toast, setToast] = React.useState<{ msg: string; ok: boolean } | null>(null);
  const [newDialog, setNewDialog] = React.useState<{ duplicateOf: HomeTheme | null } | null>(null);
  const [scheduling, setScheduling] = React.useState<HomeTheme | null>(null);
  const [editing, setEditing] = React.useState<HomeTheme | null>(null);
  const [confirm, setConfirm] = React.useState<Confirm | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [library, setLibrary] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoadError(null);
    try {
      setData(await listThemes());
    } catch (e) {
      setLoadError(errMsg(e, "Could not load themes. Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    listHomeTabs()
      .then((r) => setTabShape(r.shape))
      .catch(() => undefined);
  }, [load]);

  const run = async (t: HomeTheme, fn: () => Promise<ThemeList | unknown>, msg: string) => {
    setBusyId(t.id);
    try {
      const res = await fn();
      if (res && typeof res === "object" && "themes" in (res as any)) setData(res as ThemeList);
      else await load();
      setToast({ msg, ok: true });
    } catch (e) {
      setToast({ msg: errMsg(e, "That did not work. Try again."), ok: false });
    } finally {
      setBusyId(null);
    }
  };

  const themes = data?.themes ?? [];
  const defaultTheme = themes.find((t) => t.is_default) ?? null;
  const active = themes.find((t) => t.id === data?.active_id) ?? null;
  const defaultIsShowing = !active || active.is_default;
  const archivedCount = themes.filter((t) => t.status === "archived").length;
  const visible = themes.filter((t) => showArchived || t.status !== "archived");

  const useDefault = () => {
    if (!defaultTheme) return;
    run(defaultTheme, () => activateTheme(defaultTheme.id), "Back on the default theme. Themes turned on by hand are now drafts; scheduled ones keep their schedule.");
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1280 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/buyer-app-dashboard")}
        sx={{ textTransform: "none", color: GREEN_DARK, mb: 1, ml: -1 }}
      >
        Back to Buyer App Dashboard
      </Button>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "flex-end" }} spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 24, mb: 0.5 }}>Theme Studio</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            Festival and season looks for the top of the buyer home. Changes reach open apps within seconds.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PhotoLibraryOutlinedIcon />} onClick={() => setLibrary(true)} sx={btnOutline}>
            Asset library
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewDialog({ duplicateOf: null })} disabled={!data} sx={btnGreen}>
            New theme
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <CircularProgress sx={{ color: GREEN }} />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => { setLoading(true); load(); }} sx={{ textTransform: "none" }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : (
        <>
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Showing now for users everywhere</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
                  {active ? active.name : "Default Green"}
                  {active && (
                    <Chip
                      size="small"
                      label={statusChip(active).label}
                      sx={{ ml: 1, bgcolor: statusChip(active).bg, color: statusChip(active).color, fontWeight: 600, verticalAlign: "middle" }}
                    />
                  )}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                  Users in a city with its own theme may see that instead.
                </Typography>
              </Box>
              {!defaultIsShowing && defaultTheme && (
                <Tooltip title="Switches off themes turned on by hand. Scheduled themes keep their schedule.">
                  <span>
                    <Button variant="outlined" onClick={useDefault} disabled={busyId !== null} sx={btnOutline}>
                      Use default
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Card>

          <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1 }}>
              Themes{" "}
              <Typography component="span" sx={{ color: "text.secondary", fontWeight: 400, fontSize: 14 }}>
                ({visible.length})
              </Typography>
            </Typography>
            {archivedCount > 0 && (
              <FormControlLabel
                control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 13.5 }}>Show archived ({archivedCount})</Typography>}
              />
            )}
          </Stack>

          {visible.length === 0 ? (
            <Typography sx={{ color: "text.secondary", py: 4 }}>No themes yet. Create one to get started.</Typography>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 2 }}>
              {visible.map((t) => (
                <ThemeCard
                  key={t.id}
                  theme={t}
                  showingNow={data?.active_id === t.id}
                  defaultIsShowing={defaultIsShowing}
                  tabShape={tabShape}
                  busy={busyId === t.id}
                  onEdit={() => setEditing(t)}
                  onActivate={() =>
                    setConfirm({
                      title: `Activate “${t.name}” now?`,
                      body:
                        "It goes live for users right away and stays on until you turn it off. Any other theme you turned on by hand goes back to draft." +
                        (t.starts_at || t.ends_at ? " Its saved schedule is cleared." : ""),
                      confirmLabel: "Activate now",
                      run: () => run(t, () => activateTheme(t.id), `“${t.name}” is live now.`),
                    })
                  }
                  onUseDefault={useDefault}
                  onSchedule={() => setScheduling(t)}
                  onTurnOff={() =>
                    run(t, () => deactivateTheme(t.id), t.status === "archived" ? `“${t.name}” moved to drafts.` : `“${t.name}” is off.`)
                  }
                  onDuplicate={() => setNewDialog({ duplicateOf: t })}
                  onArchive={() =>
                    setConfirm({
                      title: `Archive “${t.name}”?`,
                      body: "It stops showing in the app and is hidden from this list. You can bring it back from “Show archived”.",
                      confirmLabel: "Archive",
                      run: () => run(t, () => archiveTheme(t.id), `“${t.name}” archived.`),
                    })
                  }
                  onDelete={() =>
                    setConfirm({
                      title: `Delete “${t.name}”?`,
                      body: "This removes the theme for good. Uploaded files stay in the asset library.",
                      confirmLabel: "Delete",
                      danger: true,
                      run: async () => {
                        await run(t, () => deleteTheme(t.id), `“${t.name}” deleted.`);
                      },
                    })
                  }
                  onReset={() =>
                    setConfirm({
                      title: `Reset “${t.name}” to its preset?`,
                      body: "All look changes (colours, layers, effects, greeting) go back to the original preset. Schedule, priority and cities are kept.",
                      confirmLabel: "Reset",
                      danger: true,
                      run: () => run(t, () => resetTheme(t.id), `“${t.name}” reset to its preset.`),
                    })
                  }
                />
              ))}
            </Box>
          )}
        </>
      )}

      {newDialog && data && (
        <NewThemeDialog
          data={data}
          tabShape={tabShape}
          duplicateOf={newDialog.duplicateOf}
          onClose={() => setNewDialog(null)}
          onCreated={(t) => {
            setNewDialog(null);
            setToast({ msg: `Created “${t.name}” as a draft.`, ok: true });
            load();
            setEditing(t);
          }}
        />
      )}

      {scheduling && (
        <ScheduleDialog
          theme={scheduling}
          onClose={() => setScheduling(null)}
          onDone={(list, msg) => {
            setData(list);
            setScheduling(null);
            setToast({ msg, ok: true });
          }}
        />
      )}

      {editing && (
        <ThemeEditor
          key={editing.id}
          theme={editing}
          tabShape={tabShape}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setToast({ msg, ok: true });
            load();
          }}
        />
      )}

      {library && <AssetLibrary open onClose={() => setLibrary(false)} />}

      <Dialog open={!!confirm} onClose={confirmBusy ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{confirm?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>{confirm?.body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm?.danger ? "error" : "primary"}
            disabled={confirmBusy}
            sx={confirm?.danger ? { textTransform: "none" } : btnGreen}
            onClick={async () => {
              if (!confirm) return;
              setConfirmBusy(true);
              try {
                await confirm.run();
              } finally {
                setConfirmBusy(false);
                setConfirm(null);
              }
            }}
          >
            {confirmBusy ? "Working…" : confirm?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert severity={toast.ok ? "success" : "error"} onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default ThemeStudio;
